import { z } from 'zod/v4';

export const MetricHistoryEntityTypeNameSchema = z.enum(['ots_component', 'off_the_shelf_part']);
export type MetricHistoryEntityTypeName = z.infer<typeof MetricHistoryEntityTypeNameSchema>;

export const EntityReadinessSchema = z.enum(['ready', 'syncing', 'never_synced', 'no_entities']);
export type EntityReadiness = z.infer<typeof EntityReadinessSchema>;

export const MetricHistoryCellStateSchema = z.enum(['stored', 'partial', 'missing', 'incomplete', 'queued', 'failed']);
export type MetricHistoryCellState = z.infer<typeof MetricHistoryCellStateSchema>;

export const MetricHistoryJobSchema = z.object({
    id: z.number(),
    state: z.enum(['pending', 'running', 'succeeded', 'failed']),
    planned: z.boolean(),
    full: z.boolean(),
    reason: z.string().nullable(),
    shardsTotal: z.number(),
    shardsDone: z.number(),
    shardsDead: z.number(),
    updatedAt: z.number(),
});
export type MetricHistoryJob = z.infer<typeof MetricHistoryJobSchema>;

export const MetricHistoryCellSchema = z.object({
    week: z.string(),
    state: MetricHistoryCellStateSchema,
    rows: z.number(),
    entities: z.number(),
    firstWrittenAt: z.number().nullable(),
    lastWrittenAt: z.number().nullable(),
    job: MetricHistoryJobSchema.nullable(),
});
export type MetricHistoryCell = z.infer<typeof MetricHistoryCellSchema>;

export const MetricHistoryEntityTypeSchema = z.object({
    entityType: MetricHistoryEntityTypeNameSchema,
    entities: z.number(),
    readiness: EntityReadinessSchema,
    lastSyncAt: z.number().nullable(),
});
export type MetricHistoryEntityType = z.infer<typeof MetricHistoryEntityTypeSchema>;

export const MetricHistoryGroupSchema = z.object({
    entityType: MetricHistoryEntityTypeNameSchema,
    groupName: z.string(),
    version: z.number(),
    shardSize: z.number(),
    medianEntities: z.number().nullable(),
    cells: z.array(MetricHistoryCellSchema),
});
export type MetricHistoryGroup = z.infer<typeof MetricHistoryGroupSchema>;

export const MetricHistorySchema = z.object({
    firstWeek: z.string(),
    lastWeek: z.string(),
    entityTypes: z.array(MetricHistoryEntityTypeSchema),
    groups: z.array(MetricHistoryGroupSchema),
});
export type MetricHistory = z.infer<typeof MetricHistorySchema>;

export const MAX_RECOMPUTE_CELLS = 500;

export const MetricHistoryCellRefSchema = z.object({
    entityType: z.string(),
    groupName: z.string(),
    week: z.string(),
});
export type MetricHistoryCellRef = z.infer<typeof MetricHistoryCellRefSchema>;

export const MetricHistoryRecomputeInputSchema = z.object({
    cells: z.array(MetricHistoryCellRefSchema).min(1).max(MAX_RECOMPUTE_CELLS),
});
export type MetricHistoryRecomputeInput = z.infer<typeof MetricHistoryRecomputeInputSchema>;

export const MetricHistoryRecomputeOutcomeSchema = z.enum(['enqueued', 'rebuilt', 'skipped']);
export type MetricHistoryRecomputeOutcome = z.infer<typeof MetricHistoryRecomputeOutcomeSchema>;

export const MetricHistoryRecomputeResultSchema = z.object({
    outcomes: z.array(MetricHistoryCellRefSchema.extend({ outcome: MetricHistoryRecomputeOutcomeSchema })),
});
export type MetricHistoryRecomputeResult = z.infer<typeof MetricHistoryRecomputeResultSchema>;
